import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animateScroll as scroll } from 'react-scroll/modules';

import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { InfoMessage } from '@eika/attention-message';
import { ButtonTypes, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { fetchOngoingApplicationEuroTaxCleared, fetchOngoingApplications } from '../../actions';
import { StringKeyValue } from '../../actions/types';
import ProductImage from '../../components/ProductImage/ProductImage';
import customHistory from '../../customHistory';
import { AppState } from '../../reducers';
import { CalculatorReducer } from '../../reducers/calculator';
import { CompletionReducer } from '../../reducers/completion';
import { getProductIcon } from '../../util/mappings';
import LoanProduct from '../../util/mappings/loanProduct';
import routePath from '../../util/mappings/routePath';

interface Props {
    completion: CompletionReducer;
    calculator: CalculatorReducer;
    text: StringKeyValue;
}

const LoanCommitmentApproved = ({ completion, calculator, text }: Props): JSX.Element => {
    const dispatch = useDispatch<any>();

    const { feature, loader } = useSelector((state: AppState) => state);
    const { salgspantActivateLoanFromLc } = feature;
    const { hasFetchedOngoingApplications } = loader;
    const { applicationId, loanCommitmentFileData } = completion.ongoing;
    const { loanAmountWithoutFees, loanProduct, loanType } = calculator.loanInfo;

    const isCampingCaravan = loanProduct === LoanProduct.campingCaravan;

    const {
        receiptApprovedLoanCommitmentTitle,
        receiptApprovedLoanCommitmentP1,
        receiptApprovedLoanCommitmentP2A,
        receiptApprovedLoanCommitmentP2B,
        receiptApprovedLoanCommitmentP3,
        receiptApprovedLoanCommitmentP4,
        receiptApprovedLoanCommitmentP5,
        receiptApprovedLoanCommitmentList1,
        receiptApprovedLoanCommitmentList2,
        receiptApprovedLoanCommitmentList3,
        receiptApprovedLoanCommitmentInfoBox,
        receiptApprovedLoanCommitmentDocumentButtonText,
        landingPageLoanUpgradeButton,
    } = text;

    const downloadPDF = (pdf: string) => {
        const linkSource = `data:application/pdf;base64,${pdf}`;
        const downloadLink = document.createElement('a');
        const fileName = 'Lånebevis.pdf';

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
    };

    useEffect(() => {
        if (!hasFetchedOngoingApplications) dispatch(fetchOngoingApplications());
        scroll.scrollToTop();
    }, [dispatch, hasFetchedOngoingApplications]);

    return (
        <>
            {receiptApprovedLoanCommitmentTitle && (
                <ProductImage
                    className="receipt__header"
                    image={getProductIcon(loanType, loanProduct)}
                    title={receiptApprovedLoanCommitmentTitle}
                />
            )}

            <div className="receipt__content">
                {receiptApprovedLoanCommitmentP1 && applicationId && (
                    <p>
                        <strong>{receiptApprovedLoanCommitmentP1}</strong> {applicationId}
                    </p>
                )}
                {receiptApprovedLoanCommitmentP2A && receiptApprovedLoanCommitmentP2B && loanAmountWithoutFees && (
                    <p>
                        {receiptApprovedLoanCommitmentP2A} {ValutaFilter(loanAmountWithoutFees, ' ', 0)},-{' '}
                        {receiptApprovedLoanCommitmentP2B}
                    </p>
                )}
                {receiptApprovedLoanCommitmentP3 && <p>{receiptApprovedLoanCommitmentP3}</p>}
                <ul>
                    {receiptApprovedLoanCommitmentList1 && <li>{receiptApprovedLoanCommitmentList1}</li>}
                    {receiptApprovedLoanCommitmentList2 && <li>{receiptApprovedLoanCommitmentList2}</li>}
                    {!isCampingCaravan && receiptApprovedLoanCommitmentList3 && (
                        <li>{receiptApprovedLoanCommitmentList3}</li>
                    )}
                </ul>
                {receiptApprovedLoanCommitmentP4 && <p>{receiptApprovedLoanCommitmentP4}</p>}
                {receiptApprovedLoanCommitmentP5 && <p>{receiptApprovedLoanCommitmentP5}</p>}
            </div>

            {!!loanCommitmentFileData && (
                <div className="receipt__cta">
                    <StandardButton
                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                        icon={IconTypes.DOKUMENT_24}
                        onClick={() => downloadPDF(loanCommitmentFileData)}
                    >
                        {receiptApprovedLoanCommitmentDocumentButtonText}
                    </StandardButton>
                </div>
            )}

            {!!salgspantActivateLoanFromLc && (
                <div className="receipt__cta">
                    <StandardButton
                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                        onClick={() => {
                            dispatch(fetchOngoingApplicationEuroTaxCleared(applicationId as string));
                            const path = routePath.loanUpgrade.replace(':appId', applicationId as string);
                            customHistory.push(path);
                        }}
                    >
                        {landingPageLoanUpgradeButton}
                    </StandardButton>
                </div>
            )}

            {receiptApprovedLoanCommitmentInfoBox && (
                <InfoMessage
                    className="receipt__info"
                    text={receiptApprovedLoanCommitmentInfoBox}
                    icon={IconTypes.LYSPAERE_24}
                />
            )}
        </>
    );
};
export default LoanCommitmentApproved;
